import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { Checkbox, TextField } from '@material-ui/core'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import {
  useGetAssignedCompanyQuery,
  useGetCompanyNameQuery,
  useGetDebtorAccountLazyQuery,
} from 'generated/graphql'
import { CommonYupValidation } from 'helpers/Form/YupValidation'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router'
import * as yup from 'yup'

interface ARConsolidatedEInvoiceParamsProps {
  CompanyID: string
  StartDocDate: Date
  EndDocDate: Date
  StartConsolidatedDate: Date
  EndConsolidatedDate: Date
  ConsolidatedDocNo: string
  DocNo: string
  DocType: string
  DebtorAccountID: string
}

interface CompanyOption {
  Name: string
  CompanyID: string
}

export const ARConsolidatedEInvoiceParamsForm = (props: any) => {
  let history = useHistory()
  const { CompanyID }: any = useParams()
  let location = useLocation()
  const editData = location?.state as any
  const latestCompany = localStorage.getItem('latestCompany')
  const ParamsSchema = yup.object().shape({
    CompanyID: CommonYupValidation.requireField('Company is required'),
  })
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />
  let user = JSON.parse(localStorage.getItem('loggedInUser'))

  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)

  const {
    handleSubmit,
    register,
    errors,
    control,
    getValues,
    watch,
    setValue,
  } = useForm<ARConsolidatedEInvoiceParamsProps>({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(ParamsSchema),
  })

  const [
    loadDebtorAccountCompanyAssignment,
    {
      loading: DebtorAccountLoading,
      data: { getDebtorAccount } = {
        getDebtorAccount: [],
      },
    },
  ] = useGetDebtorAccountLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  const {
    loading: CompanyLoading,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    variables: { CompanyID },
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (latestCompany) {
        loadDebtorAccountCompanyAssignment({
          variables: { CompanyID: latestCompany },
        })
      }
    },
  })

  const {
    loading: CompanyAssignedLoading,
    data: { getAssignedCompanyByUser } = { getAssignedCompanyByUser: [] },
  } = useGetAssignedCompanyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (latestCompany) {
        loadDebtorAccountCompanyAssignment({
          variables: { CompanyID: latestCompany },
        })
      }
    },
  })

  const [selectedDebtorAccount, setSelectedDebtorAccount]: any = useState(
    new Set()
  )

  const handleCheckboxDebtorAccountChange = (event: any) => {
    let creditorAccount = selectedDebtorAccount

    if (event?.target?.checked) {
      creditorAccount.add(event?.target?.value)
    } else {
      creditorAccount.delete(event?.target?.value)
    }
    setSelectedDebtorAccount(new Set(creditorAccount))
  }

  const onSubmit = data => {
    history.push({
      pathname: `/digital-reports/account-receivable/ar-consolidated-einvoice/generated`,
      state: {
        CompanyID: data?.CompanyID,
        StartDocDate: new Date(data.StartDocDate),
        EndDocDate: new Date(data.EndDocDate),
        StartConsolidatedDate: data.StartConsolidatedDate
          ? new Date(data.StartConsolidatedDate)
          : undefined,
        EndConsolidatedDate: data.EndConsolidatedDate
          ? new Date(data.EndConsolidatedDate)
          : undefined,
        ConsolidatedDocNo: data?.ConsolidatedDocNo,
        DocNo: data?.DocNo,
        DocType: data?.DocType,
        DebtorAccountID: selectedDebtorAccount
          ? Array.from(selectedDebtorAccount)
          : undefined,
      },
    })
  }

  return (
    <>
      {CompanyLoading && <Loading />}
      {DebtorAccountLoading && <Loading />}
      {CompanyAssignedLoading && <Loading />}
      <MainHeader
        mainBtn="back"
        onClick={() => history.push(`/digital-reports/account-receivable`)}
        smTitle={'Account Receivable'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Submenu' },
          { name: 'Consolidated E-Invoice', current: true },
        ]}
        rightRouteSegments={[{ name: 'Parameters', current: true }]}
      />
      <ContentWrapper float>
        <CardContents>
          {!CompanyAssignedLoading && !CompanyLoading && (
            <Controller
              render={({ value, onChange }) => {
                const options: CompanyOption[] =
                  !!user?.superUser === false
                    ? getAssignedCompanyByUser
                    : getCompany
                const defVal = getCompany?.filter(
                  x => x?.CompanyID === latestCompany
                )[0]
                return (
                  <Autocomplete
                    options={options || []}
                    getOptionLabel={(option: CompanyOption) => option.Name}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('CompanyID', newValue?.CompanyID)

                      loadDebtorAccountCompanyAssignment({
                        variables: { CompanyID: newValue?.CompanyID },
                      })
                    }}
                    renderOption={(props, option) => {
                      return <span>{props?.Name}</span>
                    }}
                    defaultValue={defVal}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.CompanyID?.message}
                            error={errors?.CompanyID ? true : false}
                            label="Company"
                            style={{ width: '100%' }}
                            margin="normal"
                            required
                            defaultValue={defVal}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Company"
              name="CompanyID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              helperText={errors?.CompanyID?.message}
              error={errors?.CompanyID ? true : false}
              required
              defaultValue={latestCompany}
            />
          )}
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              className="left"
              required
              as={KeyboardDatePicker}
              name="StartDocDate"
              label="Start Doc Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('StartDocDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.StartDocDate ?? new Date()}
              helperText={errors?.StartDocDate?.message}
              error={errors?.StartDocDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              className="right"
              required
              as={KeyboardDatePicker}
              name="EndDocDate"
              label="End Doc Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('EndDocDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.EndDocDate ?? new Date()}
              helperText={errors?.EndDocDate?.message}
              error={errors?.EndDocDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              className="left"
              as={KeyboardDatePicker}
              name="StartConsolidatedDate"
              label="Start Consolidated Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('StartConsolidatedDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.StartConsolidatedDate ?? null}
              helperText={errors?.StartConsolidatedDate?.message}
              error={errors?.StartConsolidatedDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              className="right"
              as={KeyboardDatePicker}
              name="EndConsolidatedDate"
              label="End Consolidated Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('EndConsolidatedDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.EndConsolidatedDate ?? null}
              helperText={errors?.EndConsolidatedDate?.message}
              error={errors?.EndConsolidatedDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="ConsolidatedDocNo"
            fullWidth
            label="Consolidated Document No"
            control={control}
            ref={register}
          />

          <Controller
            as={TextField}
            name="DocNo"
            fullWidth
            label="Document No"
            control={control}
            ref={register}
          />

          <Controller
            as={TextField}
            name="DocType"
            fullWidth
            label="Document Type"
            control={control}
            ref={register}
          />

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  disableCloseOnSelect
                  multiple
                  options={getDebtorAccount || []}
                  getOptionLabel={option => option?.DebtorName}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selecteted = new Set()

                    newValue?.map(x => {
                      selecteted.add(x?.DebtorAccountID)
                    })

                    setSelectedDebtorAccount(new Set(selecteted))
                  }}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option?.DebtorAccountID}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxDebtorAccountChange(event)
                        }}
                      />
                      {option?.DebtorName}
                    </React.Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Debtor Account"
                          margin="normal"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            name="DebtorAccountID"
            label="Debtor Account"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />
        </CardContents>

        <AccountFooter
          options={[
            {
              name: 'Submit',
              onClick: () => {
                handleSubmit(onSubmit)()
              },
              color: 'primary',
            },
          ]}
        />
      </ContentWrapper>
      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
    </>
  )
}
